import { storage } from "@/lib/storage";

export class TokenError extends Error {
  constructor(
    message: string,
    public code: "login_required" | "consent_required" | "unknown"
  ) {
    super(message);
    this.name = "TokenError";
  }
}

/**
 * Does NOT verify the signature (the server does). A token we can't parse
 * (opaque, malformed, no exp) is treated as NOT expired: the server will reject
 * it and the interceptor handles that, so this never makes a still-valid
 * session (or an opaque dev token) look expired.
 */
export function isJwtExpired(token: string): boolean {
  const expiresAt = getJwtExpiryMs(token);
  return expiresAt !== null && expiresAt <= Date.now();
}

export function getJwtExpiryMs(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    if (typeof payload.exp !== "number") return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

// Dev-only: set after an explicit logout so the startup seed below doesn't
// silently sign the developer back in on the next reload.
const DEV_LOGGED_OUT_KEY = "appio_dev_logged_out";

/** sessionStorage, so a fresh tab/window starts seeded again. */
export function markDevLoggedOut(): void {
  if (!import.meta.env.DEV) return;
  try {
    sessionStorage.setItem(DEV_LOGGED_OUT_KEY, "1");
  } catch {
    // sessionStorage unavailable (private mode quota, etc.): nothing to do.
  }
}

/**
 * Seeds the dev JWT through the normal session path, so nothing else (the
 * guard, `getAccessToken`) needs dev special-casing. Call once at startup,
 * before React first reads the token.
 */
export function seedDevAuthToken(): void {
  if (!import.meta.env.DEV) return;
  const devJwt = import.meta.env.VITE_DEV_AUTH_JWT;
  if (!devJwt) return;
  if (sessionStorage.getItem(DEV_LOGGED_OUT_KEY)) return;
  if (!storage.getAuthToken()) {
    storage.setAuthToken(devJwt);
  }
}

export async function getAccessToken(): Promise<string> {
  const token = storage.getAuthToken();
  if (!token) {
    throw new TokenError("Not authenticated", "unknown");
  }
  return token;
}
